import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { ModalHeaderWrapper, SubmitAreaStyled, FieldRow } from '../styledComponents';
import Button from '../../../components/Buttons/Button';
import ErrorModal from '../../../components/ErrorModal';
import BaseModal from '../../../components/BaseModal';
import Switch from '../../../components/Switch';
import Input from '../../../components/Input';
import doFetch from '../../../api/doFetch';

import { VERIFICATION_FIELDS } from '../constants';

const savePartner = async (data, partner) => {
  const isEdit = Boolean(partner);
  const url = '/lotlenz/partners' + (isEdit ? `/${partner.id}` : ``);
  const method = isEdit ? 'PUT' : 'POST';

  return doFetch(url, {
    method,
    body: JSON.stringify(data)
  });
};

export default function PartnerModal({ mode, isOpen, partner, toggleHandler, reloadPartners }) {
  const isEditMode = Boolean(partner);

  const { register, handleSubmit, formState, control, reset } = useForm({
    mode: 'onChange'
  });

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const defaults = {
        name: partner?.name || '',
        rateLimit: partner?.rateLimit || '',
        kmsSignatureEnabled: Boolean(partner?.kmsSignatureEnabled)
      };

      VERIFICATION_FIELDS.forEach((field) => {
        const hasValue = partner ? Boolean(partner?.responseTemplate?.[field.dbKey]) : true;
        defaults[`enable_${field.dbKey}`] = hasValue;
      });

      reset(defaults);
    }
  }, [isOpen, partner, reset]);

  const onFormSubmit = async (formData) => {
    try {
      const payload =
        mode === 'GLOBAL'
          ? {
              name: formData.name,
              rateLimit: formData.rateLimit ? Number(formData.rateLimit) : null,
              kmsSignatureEnabled: Boolean(partner?.kmsSignatureEnabled),
              responseTemplate: partner?.responseTemplate
            }
          : {
              name: partner.name,
              rateLimit: partner.rateLimit,
              responseTemplate: {}
            };

      if (mode === 'KEYS') {
        payload.kmsSignatureEnabled = formData.kmsSignatureEnabled;
        VERIFICATION_FIELDS.forEach((field) => {
          const isEnabled = formData[`enable_${field.dbKey}`];
          if (isEnabled) payload.responseTemplate[field.dbKey] = isEnabled;
        });
      }

      const { statusCode, response } = await savePartner(payload, partner);

      if (statusCode >= 400) throw new Error(response.message);

      setErrorMessage(null);
      reloadPartners();
      closeHandler();
    } catch (error) {
      setErrorMessage(error.message);
      setIsErrorModalOpen(true);
    }
  };

  const closeHandler = () => {
    toggleHandler();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      toggleHandler={closeHandler}
      header={<ModalHeaderWrapper>{isEditMode ? 'EDIT' : 'CREATE'} PARTNER</ModalHeaderWrapper>}
    >
      <ErrorModal isOpen={isErrorModalOpen} errorMessage={errorMessage} />

      <form onSubmit={handleSubmit(onFormSubmit)} style={{ minWidth: 300 }}>
        {mode === 'GLOBAL' && (
          <>
            <Input label='Partner name' name='name' required ref={register({ required: true })} />
            <Input
              label='Messages/min limit'
              type='number'
              name='rateLimit'
              required
              ref={register({ required: true })}
            />
          </>
        )}

        {mode === 'KEYS' && (
          <>
            <FieldRow>
              <Controller
                name='kmsSignatureEnabled'
                control={control}
                render={({ onChange, value }) => (
                  <Switch
                    label={value ? 'Disable KMS' : 'Enable KMS'}
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </FieldRow>
            {VERIFICATION_FIELDS.map((field) => {
              const switchName = `enable_${field.dbKey}`;

              return (
                <FieldRow key={field.name}>
                  <Controller
                    name={switchName}
                    control={control}
                    render={({ onChange, value }) => (
                      <Switch
                        label={field.name}
                        checked={value || false}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                </FieldRow>
              );
            })}
          </>
        )}

        <SubmitAreaStyled>
          <Button disabled={!formState.isValid} type='submit'>
            {isEditMode ? 'Save changes' : 'Create'}
          </Button>
        </SubmitAreaStyled>
      </form>
    </BaseModal>
  );
}
